import { useEffect, useState } from "react";
import { api, PRODUCT_CATEGORIES, resolveImage, formatTL, effectivePrice, DELIVERY_PLATFORMS } from "@/lib/api";
import { X, Upload, Loader2, Plus, Tag } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "",
  price: "",
  delivery_price: "",
  platform_prices: {},
  description: "",
  category: PRODUCT_CATEGORIES[0],
  image_url: "",
  active: true,
  removables: [],
  extras: [],
  drinks: [],
  discount_active: false,
  discount_type: "percent",
  discount_value: "",
  campaign_name: "",
};

export const ProductFormModal = ({ open, onClose, onSaved, editing }) => {
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cats, setCats] = useState(PRODUCT_CATEGORIES);
  const [removableInput, setRemovableInput] = useState("");
  const [extraName, setExtraName] = useState("");
  const [extraPrice, setExtraPrice] = useState("");
  const [drinkName, setDrinkName] = useState("");
  const [drinkPrice, setDrinkPrice] = useState("");

  const addRemovable = () => {
    const v = removableInput.trim();
    if (!v) return;
    setForm((f) =>
      f.removables.includes(v) ? f : { ...f, removables: [...f.removables, v] }
    );
    setRemovableInput("");
  };

  const removeRemovable = (item) => {
    setForm((f) => ({ ...f, removables: f.removables.filter((r) => r !== item) }));
  };

  const addExtra = () => {
    const name = extraName.trim();
    const price = parseFloat(extraPrice);
    if (!name) {
      toast.error("Ekstra malzeme adı girin");
      return;
    }
    if (isNaN(price) || price < 0) {
      toast.error("Geçerli bir fiyat girin");
      return;
    }
    setForm((f) =>
      f.extras.some((e) => e.name === name)
        ? f
        : { ...f, extras: [...f.extras, { name, price }] }
    );
    setExtraName("");
    setExtraPrice("");
  };

  const removeExtra = (name) => {
    setForm((f) => ({ ...f, extras: f.extras.filter((e) => e.name !== name) }));
  };

  const addDrink = () => {
    const name = drinkName.trim();
    const price = parseFloat(drinkPrice);
    if (!name) {
      toast.error("İçecek adı girin");
      return;
    }
    if (isNaN(price) || price < 0) {
      toast.error("Geçerli bir fiyat girin (ücretsizse 0 yazın)");
      return;
    }
    setForm((f) =>
      f.drinks.some((d) => d.name === name)
        ? f
        : { ...f, drinks: [...f.drinks, { name, price }] }
    );
    setDrinkName("");
    setDrinkPrice("");
  };

  const removeDrink = (name) => {
    setForm((f) => ({ ...f, drinks: f.drinks.filter((d) => d.name !== name) }));
  };

  useEffect(() => {
    if (open) {
      setForm(
        editing
          ? {
              name: editing.name,
              price: editing.price,
              delivery_price: editing.delivery_price ?? "",
              platform_prices: editing.platform_prices || {},
              description: editing.description || "",
              category: editing.category,
              image_url: editing.image_url || "",
              active: editing.active !== false,
              removables: editing.removables || [],
              extras: editing.extras || [],
              drinks: editing.drinks || [],
              discount_active: editing.discount_active || false,
              discount_type: editing.discount_type || "percent",
              discount_value: editing.discount_value ?? "",
              campaign_name: editing.campaign_name || "",
            }
          : empty
      );
    }
  }, [open, editing]);

  useEffect(() => {
    if (!open) return;
    api
      .get("/categories")
      .then((res) => {
        const names = res.data.map((c) => c.name);
        if (names.length) setCats(names);
      })
      .catch(() => {});
  }, [open]);

  if (!open) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setPlatformPrice = (key, v) =>
    setForm((f) => ({ ...f, platform_prices: { ...(f.platform_prices || {}), [key]: v } }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setField("image_url", res.data.image_url);
      toast.success("Görsel yüklendi");
    } catch (err) {
      toast.error("Görsel yüklenemedi");
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.price) {
      toast.error("Ürün adı ve fiyat zorunludur");
      return;
    }
    setSaving(true);
    const cleanedPlatform = {};
    Object.entries(form.platform_prices || {}).forEach(([k, v]) => {
      const n = parseFloat(v);
      if (!isNaN(n) && n > 0) cleanedPlatform[k] = n;
    });
    const payload = { ...form, price: parseFloat(form.price), delivery_price: parseFloat(form.delivery_price) || 0, platform_prices: cleanedPlatform, discount_value: parseFloat(form.discount_value) || 0 };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Ürün güncellendi");
      } else {
        await api.post("/products", payload);
        toast.success("Ürün eklendi");
      }
      onSaved();
    } catch (err) {
      toast.error("Kaydedilemedi");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-black/50" />
      <div
        data-testid="product-form-modal"
        className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto fb-scroll animate-fade-up"
      >
        <header className="sticky top-0 bg-white flex items-center justify-between px-5 h-16 border-b border-[var(--fb-border)]">
          <h2 className="font-display font-bold text-lg">
            {editing ? "Ürünü Düzenle" : "Yeni Ürün Ekle"}
          </h2>
          <button
            data-testid="close-form-btn"
            onClick={onClose}
            className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)]"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <form onSubmit={submit} className="p-5 space-y-4">
          {/* image upload */}
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
              Ürün Görseli
            </label>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-24 h-24 rounded-xl bg-[var(--fb-neutral)] overflow-hidden shrink-0 grid place-items-center">
                {form.image_url ? (
                  <img
                    src={resolveImage(form.image_url)}
                    alt="önizleme"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-xs text-[var(--fb-muted)]">Önizleme</span>
                )}
              </div>
              <label
                data-testid="image-upload-label"
                className="flex items-center gap-2 cursor-pointer bg-[var(--fb-neutral)] hover:bg-[var(--fb-border)] px-4 py-2.5 rounded-full text-sm font-semibold transition-colors"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                PC'den Görsel Seç
                <input
                  data-testid="image-upload-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFile}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
              Ürün Adı *
            </label>
            <input
              data-testid="form-name"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              className="mt-1 w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
              placeholder="Klasik Burger"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
                Fiyat (TL) *
              </label>
              <input
                data-testid="form-price"
                type="number"
                step="0.01"
                value={form.price}
                onChange={(e) => setField("price", e.target.value)}
                className="mt-1 w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
                placeholder="149.90"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
                Kategori
              </label>
              <select
                data-testid="form-category"
                value={form.category}
                onChange={(e) => setField("category", e.target.value)}
                className="mt-1 w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)] bg-white"
              >
                {cats.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
              Paket Servis Fiyatı (Varsayılan) (TL)
            </label>
            <input
              data-testid="form-delivery-price"
              type="number"
              step="0.01"
              value={form.delivery_price}
              onChange={(e) => setField("delivery_price", e.target.value)}
              className="mt-1 w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
              placeholder="Boş bırakılırsa kiosk fiyatı kullanılır"
            />
            <p className="text-xs text-[var(--fb-muted)] mt-1">
              Platforma özel fiyat girilmezse paket servis bu fiyatı kullanır. Bu da boşsa kiosk fiyatı geçerli olur.
            </p>

            <label className="mt-3 block text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
              Platforma Özel Fiyatlar (TL)
            </label>
            <div className="mt-1 grid grid-cols-2 gap-3">
              {DELIVERY_PLATFORMS.map((pl) => (
                <div key={pl.key}>
                  <span className="text-xs font-semibold text-[var(--fb-muted)]">{pl.label}</span>
                  <input
                    data-testid={`form-platform-price-${pl.key}`}
                    type="number"
                    step="0.01"
                    value={form.platform_prices?.[pl.key] ?? ""}
                    onChange={(e) => setPlatformPrice(pl.key, e.target.value)}
                    className="mt-1 w-full border border-[var(--fb-border)] rounded-xl px-3 py-2 outline-none focus:border-[var(--fb-red)]"
                    placeholder="—"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <textarea
              data-testid="form-description"
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              rows={3}
              className="mt-1 w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)] resize-none"
              placeholder="Ürün açıklaması..."
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
              Çıkarılabilir Ürünler
            </label>
            <p className="text-xs text-[var(--fb-muted)] mt-0.5">
              Müşterinin siparişten çıkarabileceği malzemeler (örn. Soğan, Turşu, Domates)
            </p>
            <div className="mt-2 flex gap-2">
              <input
                data-testid="removable-input"
                value={removableInput}
                onChange={(e) => setRemovableInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addRemovable();
                  }
                }}
                placeholder="Örn. Soğan"
                className="flex-1 border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
              />
              <button
                type="button"
                data-testid="add-removable-btn"
                onClick={addRemovable}
                className="flex items-center gap-1 bg-[var(--fb-ink)] hover:bg-black text-white font-semibold px-4 rounded-xl transition-colors"
              >
                <Plus className="w-4 h-4" /> Ekle
              </button>
            </div>
            {form.removables.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {form.removables.map((item) => (
                  <span
                    key={item}
                    data-testid={`removable-chip-${item}`}
                    className="inline-flex items-center gap-1 bg-red-50 text-[var(--fb-red)] text-sm font-semibold px-3 py-1 rounded-full"
                  >
                    {item}
                    <button
                      type="button"
                      data-testid={`remove-removable-${item}`}
                      onClick={() => removeRemovable(item)}
                      className="hover:text-[var(--fb-dark-red)]"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
              Eklenebilir Malzemeler
            </label>
            <p className="text-xs text-[var(--fb-muted)] mt-0.5">
              Müşterinin ücret karşılığı ekleyebileceği ekstralar (örn. Ekstra Peynir +15 TL)
            </p>
            <div className="mt-2 flex gap-2">
              <input
                data-testid="extra-name-input"
                value={extraName}
                onChange={(e) => setExtraName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addExtra();
                  }
                }}
                placeholder="Örn. Ekstra Peynir"
                className="flex-1 border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
              />
              <input
                data-testid="extra-price-input"
                type="number"
                step="0.01"
                value={extraPrice}
                onChange={(e) => setExtraPrice(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addExtra();
                  }
                }}
                placeholder="₺"
                className="w-24 border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
              />
              <button
                type="button"
                data-testid="add-extra-btn"
                onClick={addExtra}
                className="flex items-center gap-1 bg-[var(--fb-ink)] hover:bg-black text-white font-semibold px-4 rounded-xl transition-colors"
              >
                <Plus className="w-4 h-4" /> Ekle
              </button>
            </div>
            {form.extras.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {form.extras.map((item) => (
                  <span
                    key={item.name}
                    data-testid={`extra-chip-${item.name}`}
                    className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-sm font-semibold px-3 py-1 rounded-full"
                  >
                    {item.name} +{formatTL(item.price)}
                    <button
                      type="button"
                      data-testid={`remove-extra-${item.name}`}
                      onClick={() => removeExtra(item.name)}
                      className="hover:text-green-900"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
              İçecek Seçenekleri
            </label>
            <p className="text-xs text-[var(--fb-muted)] mt-0.5">
              Menüye içecek eklerseniz müşteri kioskta bir içecek seçmeden sepete ekleyemez (ücretsizse fiyata 0 yazın, örn. Ayran +0, Kola +10)
            </p>
            <div className="mt-2 flex gap-2">
              <input
                data-testid="drink-name-input"
                value={drinkName}
                onChange={(e) => setDrinkName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addDrink();
                  }
                }}
                placeholder="Örn. Kola"
                className="flex-1 border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
              />
              <input
                data-testid="drink-price-input"
                type="number"
                step="0.01"
                value={drinkPrice}
                onChange={(e) => setDrinkPrice(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addDrink();
                  }
                }}
                placeholder="₺"
                className="w-24 border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
              />
              <button
                type="button"
                data-testid="add-drink-btn"
                onClick={addDrink}
                className="flex items-center gap-1 bg-[var(--fb-ink)] hover:bg-black text-white font-semibold px-4 rounded-xl transition-colors"
              >
                <Plus className="w-4 h-4" /> Ekle
              </button>
            </div>
            {form.drinks.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {form.drinks.map((item) => (
                  <span
                    key={item.name}
                    data-testid={`drink-chip-${item.name}`}
                    className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-sm font-semibold px-3 py-1 rounded-full"
                  >
                    {item.name} {item.price > 0 ? `+${formatTL(item.price)}` : "· Ücretsiz"}
                    <button
                      type="button"
                      data-testid={`remove-drink-${item.name}`}
                      onClick={() => removeDrink(item.name)}
                      className="hover:text-blue-900"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-[var(--fb-border)] p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                data-testid="form-discount-active"
                type="checkbox"
                checked={form.discount_active}
                onChange={(e) => setField("discount_active", e.target.checked)}
                className="w-4 h-4 accent-[var(--fb-red)]"
              />
              <span className="text-sm font-semibold flex items-center gap-1.5">
                <Tag className="w-4 h-4 text-[var(--fb-red)]" /> Kampanya / İndirim uygula
              </span>
            </label>
            {form.discount_active && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
                      İndirim Tipi
                    </label>
                    <select
                      data-testid="form-discount-type"
                      value={form.discount_type}
                      onChange={(e) => setField("discount_type", e.target.value)}
                      className="mt-1 w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)] bg-white"
                    >
                      <option value="percent">Yüzde (%)</option>
                      <option value="amount">Sabit Tutar (₺)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
                      {form.discount_type === "amount" ? "İndirim (₺)" : "İndirim (%)"}
                    </label>
                    <input
                      data-testid="form-discount-value"
                      type="number"
                      step="0.01"
                      value={form.discount_value}
                      onChange={(e) => setField("discount_value", e.target.value)}
                      className="mt-1 w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
                      placeholder={form.discount_type === "amount" ? "30" : "20"}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
                    Kampanya Adı (isteğe bağlı)
                  </label>
                  <input
                    data-testid="form-campaign-name"
                    value={form.campaign_name}
                    onChange={(e) => setField("campaign_name", e.target.value)}
                    className="mt-1 w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
                    placeholder="Örn. Hafta Sonu Fırsatı"
                  />
                </div>
                {form.price && parseFloat(form.discount_value) > 0 && (
                  <p data-testid="discount-preview" className="text-sm font-semibold">
                    Yeni fiyat:{" "}
                    <span className="text-[var(--fb-muted)] line-through">{formatTL(parseFloat(form.price))}</span>{" "}
                    <span className="text-[var(--fb-red)] font-extrabold">
                      {formatTL(
                        effectivePrice({
                          price: parseFloat(form.price),
                          discount_active: true,
                          discount_type: form.discount_type,
                          discount_value: parseFloat(form.discount_value) || 0,
                        })
                      )}
                    </span>
                  </p>
                )}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              data-testid="form-active"
              type="checkbox"
              checked={form.active}
              onChange={(e) => setField("active", e.target.checked)}
              className="w-4 h-4 accent-[var(--fb-red)]"
            />
            <span className="text-sm font-medium">Kiosk ekranında aktif olarak göster</span>
          </label>

          <button
            data-testid="save-product-btn"
            type="submit"
            disabled={saving || uploading}
            className="w-full bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] disabled:opacity-60 text-white font-bold py-3.5 rounded-full transition-colors active:scale-[0.99]"
          >
            {saving ? "Kaydediliyor..." : editing ? "Değişiklikleri Kaydet" : "Ürünü Ekle"}
          </button>
        </form>
      </div>
    </div>
  );
};
